import React from 'react';
import classNames from 'classnames';
import { isString } from 'lodash';
import { getImageUrl } from '../../../../services';
import { isIos } from '../../../../services/detect-platform';
import { createHashtagPath, createHashtagHref } from '../../../../services/search-page-url';
import { generalLinkSettings } from '../../constants/linkSettings';
import { RCE_FILE_DOWNLOAD_WILL_BEGIN_SHORTLY } from '../../constants/messages';
import { viewerCustomStyleFn, viewerCustomBackgroundStyleFn, styleSelectionPredicate, getTextColorSchema, } from '../../services/text-color-utils';
import styles from './ricos-viewer-wrapper.scss';
const MAX_PINTEREST_IMAGE_SIZE = 5000;
const defaultFallback = () => 'Content cannot be displayed';
const RicosViewer = (props) => {
    const { WixRicosViewer, content, rceVersion, theme, section, isBusinessManager = false, isSeo, isMobile, locale, isPostPageBundle, instance, postId, setViewerRef, baseUrl, iframeSandboxDomain, visitorId, fallback = defaultFallback, } = props;
    const [error, setError] = React.useState(false);
    const errorRef = React.useRef(false);
    // Callbacks handed to ricos read the latest props at call time.
    const propsRef = React.useRef(props);
    propsRef.current = props;
    // TODO: remove when ricos supports plain object
    const wixExperiments = React.useRef({ all: () => props.experiments }).current;
    const onError = React.useCallback((nextError) => {
        if (!errorRef.current) {
            errorRef.current = true;
            propsRef.current.onError?.(nextError);
            setError(nextError);
        }
    }, []);
    if (error) {
        return React.createElement(React.Fragment, null, fallback?.() ?? null);
    }
    const getImagePluginConfig = () => ({
        encoding: 'AVIF',
        imageProps: (src) => propsRef.current.pageUrl
            ? {
                'data-pin-url': propsRef.current.pageUrl,
                'data-pin-media': isString(src)
                    ? src
                    : getImageUrl({
                        image: src,
                        maxWidth: MAX_PINTEREST_IMAGE_SIZE,
                        maxHeight: MAX_PINTEREST_IMAGE_SIZE,
                    }),
            }
            : {},
    });
    const getGalleryPluginConfig = () => {
        const { pageUrl } = props;
        if (!pageUrl) {
            return {};
        }
        return {
            imageProps: () => ({ 'data-pin-url': pageUrl }),
        };
    };
    const getTextColorPluginConfig = () => {
        const { appSettings, isHexColor } = props;
        const colorScheme = getTextColorSchema({ appSettings });
        return {
            customStyleFn: viewerCustomStyleFn(colorScheme, { isHexColor }),
            styleSelectionPredicate: styleSelectionPredicate(colorScheme, {
                isHexColor,
            }),
        };
    };
    const getTextHighlightPluginConfig = () => {
        const { appSettings, isHexColor } = props;
        const colorScheme = getTextColorSchema({ appSettings });
        return {
            customStyleFn: viewerCustomBackgroundStyleFn(colorScheme, { isHexColor }),
            styleSelectionPredicate: styleSelectionPredicate(colorScheme, {
                isHexColor,
            }),
        };
    };
    const getLinkPreviewPluginConfig = () => {
        return {
            enableEmbed: true,
            enableLinkPreview: true,
            enableEnterToConvertLinks: false,
        };
    };
    const getHashtagPluginConfig = () => {
        const { hashtagNavigate, sectionUrl } = props;
        return {
            onClick: (event, text) => {
                event.preventDefault();
                event.stopPropagation();
                hashtagNavigate(createHashtagPath(text, {
                    newPath: propsRef.current.isHashtagPageAsFeedPageEnabled,
                }));
            },
            createHref: (text) => createHashtagHref(text, sectionUrl, {
                newPath: propsRef.current.isHashtagPageAsFeedPageEnabled,
            }),
        };
    };
    const getFileDownloadBIProps = (file, success) => {
        const { postId: currentPostId, isDemoPost } = propsRef.current;
        return {
            file_ext: file.type,
            file_id: file.id,
            file_size: file.size,
            mime_type: file.mimeType,
            post_stable_id: currentPostId,
            is_demo: isDemoPost,
            origin: 'post',
            success,
        };
    };
    const getFilePluginConfig = () => {
        const { bi, actions } = props;
        return {
            resolveFileUrl: (file) => {
                isIos() && actions.showMessage(RCE_FILE_DOWNLOAD_WILL_BEGIN_SHORTLY);
                return actions
                    .requestFileDownloadUrl(postId, file.path, file.id, file.mimeType)
                    .then(({ url }) => {
                    bi.fileDownloaded(getFileDownloadBIProps(file, true));
                    return url;
                })
                    .catch(() => bi.fileDownloaded(getFileDownloadBIProps(file, false)));
            },
        };
    };
    const getPollSettings = () => {
        const onBeforeVote = ({ voteRole }) => {
            const { currentUser, actions, isBusinessManager: isBm = false } = propsRef.current;
            return new Promise((resolve, reject) => {
                if (isBm) {
                    return reject();
                }
                if (currentUser || voteRole === 'ALL') {
                    return resolve();
                }
                actions.requestLogin().then(resolve, reject);
            });
        };
        return { onBeforeVote };
    };
    const plugins = [
        props.pluginAudio(),
        props.pluginCodeBlock(),
        props.pluginDivider(),
        props.pluginLinkButton(),
        props.pluginGallery(getGalleryPluginConfig()),
        props.pluginGiphy(),
        props.pluginHashtag(getHashtagPluginConfig()),
        props.pluginHtml(),
        props.pluginImage(getImagePluginConfig()),
        props.pluginLineSpacing(),
        props.pluginIndent(),
        props.pluginLink(),
        props.pluginTextColor(getTextColorPluginConfig()),
        props.pluginTextHighlight(getTextHighlightPluginConfig()),
        props.pluginLinkPreview(getLinkPreviewPluginConfig()),
        props.pluginVideo(),
        props.pluginFileUpload(getFilePluginConfig()),
        props.pluginActionButton(),
        props.pluginVerticalEmbed(),
        props.pluginTable(),
        props.pluginCollapsibleList(),
        ...(props.pluginPoll ? [props.pluginPoll(getPollSettings())] : []),
        ...(props.pluginShape ? [props.pluginShape()] : []),
        ...(props.pluginSmartBlock ? [props.pluginSmartBlock()] : []),
        ...(props.pluginToc ? [props.pluginToc()] : []),
        ...(props.pluginSlot ? [props.pluginSlot(props.slotPluginConfig)] : []),
    ];
    const className = classNames(styles.container);
    return (React.createElement("div", { className: styles.relativeContainer },
        React.createElement("div", { ref: setViewerRef, className: className, "data-rce-version": rceVersion },
            React.createElement(WixRicosViewer, { plugins: plugins, content: content, theme: theme, locale: locale, instance: instance, onError: onError, isMobile: isMobile, seoSettings: isSeo, generalLinkSettings: props.generalLinkSettings ?? generalLinkSettings, wixExperiments: wixExperiments, addAnchors: true, mediaSettings: props.mediaSettings, baseUrl: baseUrl, iframeSandboxDomain: iframeSandboxDomain, biSettings: {
                    consumer: 'Blog',
                    contentId: postId,
                    platform: isBusinessManager ? 'BM' : 'Livesite',
                    usage: isPostPageBundle ? 'Post' : section,
                    defaultParams: {
                        visitorId,
                    },
                } }))));
};
export default React.memo(RicosViewer);
//# sourceMappingURL=ricos-viewer.js.map